import React, {useEffect, useRef, useState} from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import moment from 'moment'
import useUtils from './useUtils'
import TableSkeleton from '../../modules/skeleton/TableSkelton'
import {KTSVG, toAbsoluteUrl} from '../../../_metronic/helpers'
import {useNavigate} from 'react-router-dom'
import {setTitle} from '../../redux/reducers/mainSlice'
import {useDispatch} from 'react-redux'
import formatDate from '../../../_metronic/helpers/formatDate'
import CustomFilterDropdown from '../../modules/common/CustomFilterDropdown'
import Papa, {unparse} from 'papaparse'
import {saveAs} from 'file-saver'
import CSVModal from '../../modules/modal/CSVModal'
import {useHelper} from '../../../_metronic/helpers/useHelper'
import {showAlert} from '../../redux/reducers/alertSlice'

const CalenderView = ({className, close}) => {
  const {fetchCohort, fetchFullSchedule, addCSV, submitReport, fetchAllSchedules} = useUtils()
  const {createSmartChunks} = useHelper()
  const [scheduleEvents, setScheduleEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const calendarRef = useRef(null)

  const [selectedCohort, setSelectedCohort] = useState(null)

  const [cohortOptions, setCohortOptions] = useState([])
  const [date, setDate] = useState(null)

  // Token-based pagination states
  const [cohortNextPageToken, setCohortNextPageToken] = useState(null)

  // Loading states
  const [isLoadingCohorts, setIsLoadingCohorts] = useState(false)

  // CSV functionality states
  const [uploadLoading, setUploadLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [csvData, setcsvData] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isUpload, setIsUpload] = useState(false)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  dispatch(setTitle('Schedule'))

  // Load more functions for token-based pagination
  const loadMoreCohorts = async () => {
    if (isLoadingCohorts || !cohortNextPageToken) return

    setIsLoadingCohorts(true)
    try {
      const response = await fetchCohort(cohortNextPageToken)
      if (response) {
        setCohortOptions((prev) => [...prev, ...response.records])
        setCohortNextPageToken(response.nextPageToken)
      }
    } catch (error) {
      console.error('Error loading more cohorts:', error)
    } finally {
      setIsLoadingCohorts(false)
    }
  }

  const getFullSchedule = async (start, end) => {
    try {
      const scheduleData = await fetchFullSchedule(start, end, selectedCohort)
      const events = scheduleData?.records?.map((data) => ({
        title: data?.topic,
        date: moment(Number(data?.startTime)).format('YYYY-MM-DD'),
        startTime: data?.startTime,
        endTime: data?.endTime,
        url: data?.zoomStartLink,
        urlTarget: '_blank',
      }))

      setScheduleEvents(events)
      setLoading(true)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getCurrentDate = (type) => {
    if (!type) type = 'month'

    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi()
      const currentDate = calendarApi.getDate()
      const startDate =
        type === 'month'
          ? moment(currentDate).startOf('month').valueOf()
          : type === 'week'
          ? moment(currentDate).startOf('week').valueOf()
          : moment(currentDate).startOf('day').valueOf()
      const endDate =
        type === 'month'
          ? moment(currentDate).endOf('month').valueOf()
          : type === 'week'
          ? moment(currentDate).endOf('week').valueOf()
          : moment(currentDate).endOf('day').valueOf()
      setDate({startDate, endDate})
    }
  }

  useEffect(() => {
    if (date) {
      getFullSchedule(date.startDate, date.endDate)
    }
  }, [date, selectedCohort])

  const handleDateChange = (info) => {
    const {view} = info
    const type =
      view.type === 'dayGridMonth' ? 'month' : view.type === 'dayGridWeek' ? 'week' : 'day'
    getCurrentDate(type)
  }

  const eventClickHandler = (info) => {
    if (info.event.url) {
      info.jsEvent.preventDefault()
      window.open(info.event.url, '_blank')
    }
  }

  const handleDownload = async () => {
    try {
      setDownloadLoading(true)
      const data = await fetchAllSchedules(null, selectedCohort)
      const csv = unparse(data, {header: true})
      const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
      saveAs(blob, 'livestream_schedules.csv')
    } catch (error) {
      console.error('Error downloading schedules:', error)
    } finally {
      setDownloadLoading(false)
    }
  }

  const uploadFile = async (data) => {
    let successRecords = 0
    const failedRecords = []
    try {
      const chunks = createSmartChunks(data)
      const uploadPromises = chunks.map((chunk) => addCSV(chunk))
      const response = await Promise.all(uploadPromises)

      response.forEach((response) => {
        if (response.success) {
          successRecords += response.success
        }
        if (response.failed?.length > 0) {
          failedRecords.push(...response.failed) // Aggregate failed records
        }
      })

      const successMessage = successRecords
        ? `${successRecords} livestream schedules uploaded successfully`
        : ''
      const failedMessage = failedRecords.length
        ? `${failedRecords.length} livestream schedules failed to upload`
        : ''
      const combinedMessage = [successMessage, failedMessage].filter(Boolean).join('. ')
      dispatch(showAlert({message: combinedMessage, variant: 'info', timeout: 5000}))

      const finalSummary = {
        recordType: 'Livestream Schedules',
        totalRecords: data.length,
        successRecords: successRecords,
        failedRecords: failedRecords,
      }

      const res = await submitReport(finalSummary)
      if (res) {
        dispatch(
          showAlert({message: 'Report Submitted Successfully', variant: 'info', timeout: 5000})
        )
      }
    } catch (error) {
      console.error('Error uploading schedules:', error)
    } finally {
      setUploadLoading(false)
      // Refresh calendar data
      getCurrentDate('month')
    }
  }

  const handleFileChange = (event) => {
    setUploadLoading(true)
    const file = event.target.files[0]
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        Papa.parse(file, {
          header: true,
          complete: (result) => {
            // Filter out empty rows and trim whitespace from all values
            const filteredData = result.data
              .filter((row) => Object.values(row).some((value) => value && value.trim() !== ''))
              .map((row) => {
                const trimmedRow = {}
                Object.keys(row).forEach((key) => {
                  trimmedRow[key] = typeof row[key] === 'string' ? row[key].trim() : row[key]
                })
                return trimmedRow
              })
            setIsOpen(true)
            setcsvData(filteredData)
            event.target.value = null
          },
          error: (error) => {
            console.error('Error parsing CSV:', error)
          },
        })
      } else {
        dispatch(showAlert({message: 'Please select a valid CSV file.', variant: 'danger'}))
      }
    }
  }

  useEffect(() => {
    if (csvData) {
      const transformedData = csvData?.map((obj, index) => ({
        id: obj?.id,
        topic: obj?.topic,
        cohortId: obj?.cohort,
        totalParticipantsJoined: obj?.totalParticipantsJoined,
        isForBlackforceMember:
          obj?.isForBlackforceMember === 'true' || obj?.isForBlackforceMember === true,
        zoomMeetingId: obj?.zoomMeetingId,
        zoomStartLink: obj?.zoomStartLink,
        zoomJoinLink: obj?.zoomJoinLink,
        startTime: obj?.startTime ? parseInt(obj?.startTime) : null,
        endTime: obj?.endTime ? parseInt(obj?.endTime) : null,
      }))
      uploadFile(transformedData)
      setcsvData(null)
    }
  }, [isUpload])

  useEffect(() => {
    getCurrentDate('month')
  }, [])

  const fetchInitialData = async () => {
    try {
      const cohortResponse = await fetchCohort()
      if (cohortResponse) {
        setCohortOptions(cohortResponse.records)
        setCohortNextPageToken(cohortResponse.nextPageToken)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  useEffect(() => {
    fetchInitialData()
  }, [])

  if (loading) return <TableSkeleton />

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5 px-13'>
        <h3 className='card-title align-items-start'>
          <span className=' mt-1 text-muted fs-7 '>{scheduleEvents?.length} Items</span>
        </h3>
        <div className='d-flex align-items-center justify-content-between w-100'>
          <div className='d-flex align-items-center flex-wrap gap-3'>
            <div>
              <CustomFilterDropdown
                options={cohortOptions}
                selectedValue={selectedCohort}
                onChange={setSelectedCohort}
                onScrollEnd={loadMoreCohorts}
                isLoading={isLoadingCohorts}
                placeholder='Select Cohort'
                displayField='title'
              />
            </div>
          </div>
          <div className='card-toolbar'>
            <img
              src={toAbsoluteUrl('media/icons/duotune/files/fil001.svg')}
              className='me-5'
              onClick={() => close()}
              style={{cursor: 'pointer', height: '24px', width: '24px'}}
            />

            {/* CSV Download Button */}
            <button
              onClick={handleDownload}
              disabled={downloadLoading}
              className='btn btn-sm btn-light-primary me-2'
            >
              {downloadLoading ? (
                <span className='spinner-border spinner-border-sm me-2' />
              ) : (
                <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-2' />
              )}
              <span className='text-nowrap'>Download CSV</span>
            </button>

            {/* CSV Upload Button */}
            <label className='btn btn-sm btn-light-primary me-2' style={{cursor: 'pointer'}}>
              {uploadLoading ? (
                <span className='spinner-border spinner-border-sm me-2' />
              ) : (
                <KTSVG path='/media/icons/duotune/files/fil010.svg' className='svg-icon-2' />
              )}
              <span className='text-nowrap'>Upload CSV</span>
              <input
                type='file'
                accept='.csv'
                onChange={handleFileChange}
                style={{display: 'none'}}
                disabled={uploadLoading}
              />
            </label>

            <a
              onClick={() => navigate('/schedulelist/addschedule')}
              className='btn btn-sm btn-light-primary'
            >
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
              Add Live Stream
            </a>
          </div>
        </div>
      </div>

      <div className='card-body py-3'>
        <div className='px-5 py-8'>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridDay,dayGridWeek,dayGridMonth',
            }}
            initialView='dayGridMonth'
            weekends={true}
            events={scheduleEvents}
            datesSet={handleDateChange}
            eventClick={eventClickHandler}
            titleFormat={{month: 'short', year: 'numeric'}}
          />
        </div>
      </div>

      {/* CSV Upload Modal */}
      {isOpen && (
        <CSVModal
          show={isOpen}
          handleClose={() => {
            setIsOpen(false)
          }}
          handleCancel={() => {
            setUploadLoading(false)
            setIsOpen(false)
          }}
          csvData={csvData}
          upload={() => setIsUpload((prev) => !prev)}
          optionalFields={['id']}
        />
      )}
    </div>
  )
}

export default CalenderView
